import { defineStore } from 'pinia'
import axios from '@/services/index.js'

export const useSalesclerksStore = defineStore('salesclerks', {
  modelName: 'Salesclerk',
  state: () => ({
    items: [],
    currentSalesClerk: null,
    totalItems: 0,
    currentPage: 1,
    perPage: 10,
    loading: false,
    error: null,

    // Para el componente Vendedores.vue
    comtotales: 0,
    comliqui: 0,

    // Filtros
    filters: {
      search: '',
      active: 'all'
    }
  }),

  getters: {
    showSalesclerks: (state) => state.items,
    getItems: (state) => state.items,
    getCurrentSalesClerk: (state) => state.currentSalesClerk,
    getTotalItems: (state) => state.totalItems,
    getCurrentPage: (state) => state.currentPage,
    getPerPage: (state) => state.perPage,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    getComTotales: (state) => state.comtotales,
    getComLiqui: (state) => state.comliqui,
    getFilters: (state) => state.filters
  },

  actions: {
    onModelChanged() {
      this.getSalesclerks()
    },

    // Método original mantenido para compatibilidad
    async getSalesclerks() {
      try {
        this.loading = true
        const response = await axios.get('/api/salesclerks/getSalesclerks')
        this.items = response.data.data || []
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar vendedores'
        this.items = []
      } finally {
        this.loading = false
      }
    },

    // Obtener lista de vendedores con paginación
    async fetchSalesClerks(params = {}) {
      this.loading = true
      this.error = null

      try {
        const queryParams = {
          page: this.currentPage,
          search: this.filters.search,
          active: this.filters.active,
          ...params
        }

        const response = await axios.get('/api/salesclerks/list', { params: queryParams })

        if (response.data) {
          this.items = response.data.data || []
          this.totalItems = response.data.total || 0
        }

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar vendedores'
        this.items = []
        throw error
      } finally {
        this.loading = false
      }
    },

    // Obtener un vendedor específico
    async fetchSalesClerk(id) {
      this.loading = true
      this.error = null

      try {
        const response = await axios.get(`/api/salesclerks/getSalesclerk/${id}`)

        if (response.data) {
          this.currentSalesClerk = response.data.data || response.data
        }

        return this.currentSalesClerk
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar vendedor'
        this.currentSalesClerk = null
        throw error
      } finally {
        this.loading = false
      }
    },

    // Crear o actualizar vendedor
    async saveSalesClerk(salesClerkData) {
      this.loading = true
      this.error = null

      try {
        let response

        if (salesClerkData.id) {
          // Actualizar
          response = await axios.post(`/api/salesclerks/edit/${salesClerkData.id}`, salesClerkData)
        } else {
          // Crear
          response = await axios.post('/api/salesclerks/add', salesClerkData)
        }

        if (response.data.success || response.data.sucess) {
          // Recargar lista
          await this.fetchSalesClerks()
          return response.data
        }

        throw new Error(response.data.message || 'Error al guardar vendedor')
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Error al guardar vendedor'
        throw error
      } finally {
        this.loading = false
      }
    },

    // Eliminar vendedor
    async deleteSalesClerk(id) {
      this.loading = true
      this.error = null

      try {
        const response = await axios.post('/api/salesclerks/delete', { id })

        if (response.data.success || response.data.sucess) {
          // Recargar lista
          await this.fetchSalesClerks()
          return response.data
        }

        throw new Error(response.data.message || 'Error al eliminar vendedor')
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Error al eliminar vendedor'
        throw error
      } finally {
        this.loading = false
      }
    },

    // Obtener estadísticas/índices
    async fetchIndex() {
      try {
        const response = await axios.post('/api/salesclerks/getIndex')

        if (response.data) {
          this.comtotales = response.data.total || 0
          this.comliqui = response.data.debt || 0
        }

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar estadísticas'
        throw error
      }
    },

    // Actualizar filtros
    setFilters(filters) {
      this.filters = { ...this.filters, ...filters }
    },

    // Cambiar página
    setPage(page) {
      this.currentPage = page
    },

    // Obtener balance del vendedor
    async fetchSalesClerkBalance(id) {
      try {
        const response = await axios.get(`/api/salesclerks/getBalance/${id}`)

        if (response.data.success) {
          return response.data.data || response.data
        }

        return null
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar balance del vendedor'
        throw error
      }
    },

    // Obtener comisiones del vendedor
    async fetchSalesClerkCommissions(id, params = {}) {
      try {
        const response = await axios.get(`/api/salesclerks/getCommissions/${id}`, { params })

        if (response.data && response.data.data) {
          return response.data.data
        }

        return []
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar comisiones'
        throw error
      }
    },

    // Liquidar por monto
    async liquidateByAmount(clerkId, amount, fundId) {
      try {
        const response = await axios.post('/api/salesclerks/newDischarge', {
          clerk_id: clerkId,
          amount: amount,
          fund_origin: fundId
        })

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al liquidar comisión'
        throw error
      }
    },

    // Liquidar una comisión específica
    async dischargeOne(commissionId, fundOrigin) {
      try {
        const response = await axios.post(`/api/salesclerks/dischargeOne/${commissionId}`, {
          fund_origin: fundOrigin
        })

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al liquidar comisión'
        throw error
      }
    },

    // Eliminar una comisión
    async removeCommission(commissionId) {
      try {
        const response = await axios.get(`/api/salesclerks/deleteCommission/${commissionId}`)

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al eliminar comisión'
        throw error
      }
    },

    // Limpiar store
    clearStore() {
      this.items = []
      this.currentSalesClerk = null
      this.totalItems = 0
      this.currentPage = 1
      this.comtotales = 0
      this.comliqui = 0
      this.error = null
    }
  }
})